import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useCache } from '../contexts/DataCacheContext';
import { getWorkoutWithExercises, saveCompleteWorkout, getLastPerformedForExercises, getFirstPerformedForExercises, getExerciseProgressHistory, awardWorkoutXP, evaluateBadgesAfterWorkout } from '../lib/database';
import { buildWorkoutSummary } from '../lib/workoutSummary';
import { shouldShowPushPrePrompt, markPushPrePromptDone } from '../lib/pushPrePrompt';
import RestPushPrePrompt from './RestPushPrePrompt';
import { isTimeType, isCardioType } from '../lib/exerciseType';
import { seedFromProgramExercises, getSupersetForExercise, sharedRounds, isRoundComplete, resolveRestSeconds } from '../lib/sessionSupersets';
import { newClientSessionId } from '../lib/clientSessionId';
import { unlockAudioAlert } from '../lib/restAlert';
import { cancelPushForRest } from '../lib/restPush';
import { devLog } from '../lib/log';
import { useSupersetLinking } from '../hooks/useSupersetLinking';
import SupersetLinkBar from './SupersetLinkBar';
import SupersetList from './SupersetList';
import AddActionsSheet from './AddActionsSheet';
import { supabase } from '../lib/supabase';
import { generateWeightSuggestion, formatSuggestion } from '../lib/progressUtils';
import { calculateWorkoutXP } from '../lib/xpUtils';
import ExerciseExecutionCard from './ExerciseExecutionCard';
import RestTimer, { clearPersistedRestTimer } from './RestTimer';
import AddExerciseSheet from './AddExerciseSheet';

function WorkoutExecution() {
  const { workoutId } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const toast = useToast();
  const { invalidate: invalidateCache } = useCache();

  const [workout, setWorkout] = useState(null);

  // Helper to find saved session in localStorage
  const findSavedSession = () => {
    if (!workoutId) return null;
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(`workout-session-${workoutId}-`)) {
          const saved = localStorage.getItem(key);
          if (saved) {
            const data = JSON.parse(saved);
            const sessionAge = Date.now() - data.lastUpdate;
            if (sessionAge < 24 * 60 * 60 * 1000) {
              return data;
            }
          }
        }
      }
    } catch (e) {
      console.error('Error reading from localStorage:', e);
    }
    return null;
  };

  // Initialize startTime from localStorage if available
  const [startTime, setStartTime] = useState(() => {
    const saved = findSavedSession();
    if (saved?.startTime) {
      /*#__PURE__*/ devLog('⏰ [WORKOUT] Initializing startTime from localStorage');
      return saved.startTime;
    }
    return Date.now();
  });
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Initialize exerciseStates from localStorage if available
  const [exerciseStates, setExerciseStates] = useState(() => {
    const saved = findSavedSession();
    if (saved?.exerciseStates && Object.keys(saved.exerciseStates).length > 0) {
      /*#__PURE__*/ devLog('💪 [WORKOUT] Initializing exerciseStates from localStorage');
      return saved.exerciseStates;
    }
    return {};
  });

  // Initialize extraExercises from localStorage if available
  const [extraExercises, setExtraExercises] = useState(() => {
    const saved = findSavedSession();
    if (saved?.extraExercises && saved.extraExercises.length > 0) {
      /*#__PURE__*/ devLog('➕ [WORKOUT] Initializing extraExercises from localStorage');
      return saved.extraExercises;
    }
    return [];
  });
  // Superserie di sessione (effimere). null = mai inizializzate → verranno
  // seminate dalle superserie di scheda (superset_group) al caricamento.
  const [sessionSupersets, setSessionSupersets] = useState(() => {
    const saved = findSavedSession();
    return saved?.sessionSupersets ?? null;
  });
  const [showExtraExerciseModal, setShowExtraExerciseModal] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false); // menu del pulsante "+"
  const [saving, setSaving] = useState(false);
  // Guardia sincrona contro il doppio submit: lo stato React `saving` si
  // aggiorna solo al re-render, un secondo tap nello stesso frame lo bypassa.
  const savingRef = useRef(false);
  // PB-013: identità stabile della sessione (chiave di idempotency DB).
  // Generata UNA volta per sessione reale (lazy init, mai nei render), ripresa
  // dal localStorage al restore — anche per sessioni legacy senza UUID — e
  // eliminata solo col clear della sessione dopo salvataggio riuscito.
  const [clientSessionId] = useState(() => {
    const saved = findSavedSession();
    return saved?.clientSessionId || newClientSessionId();
  });
  const [loading, setLoading] = useState(true);
  const [exerciseHistory, setExerciseHistory] = useState({}); // Last performed data for each exercise
  const [firstPerformed, setFirstPerformed] = useState({}); // First performed data (starting weight)
  const [weightSuggestions, setWeightSuggestions] = useState({}); // Weight suggestions for each exercise

  // Accordion state - which exercise is expanded (null = all collapsed, or specific exercise id)
  const [expandedExerciseId, setExpandedExerciseId] = useState(null); // Start with all collapsed

  // Rest Timer state
  const [isRestTimerActive, setIsRestTimerActive] = useState(false);
  const [restTimerSeconds, setRestTimerSeconds] = useState(90);
  const [timerKey, setTimerKey] = useState(0);
  // Pre-prompt notifiche: proposto al primo recupero della vita dell'utente
  const [showPushPrePrompt, setShowPushPrePrompt] = useState(false);

  // Calculate storage key dynamically (profile may be null on first render)
  const getStorageKey = () => `workout-session-${workoutId}-${profile?.id}`;

  // Guardia contro setState dopo unmount (il caricamento storico avviene in
  // background via setTimeout+Promise, che possono risolversi a pagina chiusa)
  const mountedRef = useRef(true);
  const historyTimerRef = useRef(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(historyTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (workoutId && profile?.id) {
      // Check if we already have state from localStorage lazy init
      const hasExistingState = Object.keys(exerciseStates).length > 0;

      if (hasExistingState && workout) {
        // Already have state and workout, skip loading
        /*#__PURE__*/ devLog('🏋️ [WORKOUT EXECUTION] Already have state, skipping reload');
        setLoading(false);
      } else {
        // Need to load workout data (but will preserve exerciseStates if already set)
        loadWorkoutData(hasExistingState);
      }
    }
  }, [workoutId, profile]);

  // Timer effect
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  // Auto-save to localStorage effect (include extraExercises!)
  useEffect(() => {
    if (!loading && Object.keys(exerciseStates).length > 0) {
      const saveData = {
        startTime,
        exerciseStates,
        extraExercises, // CRITICAL: Save extra exercises to prevent data loss on refresh
        sessionSupersets: sessionSupersets ?? [], // superserie di sessione
        clientSessionId, // PB-013: identità della sessione, stabile tra reload/retry
        lastUpdate: Date.now()
      };
      localStorage.setItem(getStorageKey(), JSON.stringify(saveData));
    }
  }, [exerciseStates, extraExercises, sessionSupersets, startTime, loading, clientSessionId]);

  // Seed delle superserie di sessione dalle superserie di scheda (una sola volta).
  // null = mai inizializzate (nessuna sessione salvata) → importa dai superset_group.
  useEffect(() => {
    if (sessionSupersets === null && workout?.exercises) {
      // PB-011: il recupero del gruppo viene dalla proprietà della superserie
      // (workout.supersetRestByLetter), non dal primo esercizio.
      setSessionSupersets(seedFromProgramExercises(workout.exercises, workout.supersetRestByLetter));
    }
  }, [workout, sessionSupersets]);

  // Warn before leaving if unsaved data
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasCompletedSets()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [exerciseStates]);

  // Handle visibility change and page restore from bfcache
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        /*#__PURE__*/ devLog('👁️ [WORKOUT EXECUTION] App became visible');
        // Force recalculate elapsed time immediately
        setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));
      }
    };

    // Handle page restore from bfcache (iOS Safari often uses this)
    const handlePageShow = (event) => {
      if (event.persisted) {
        /*#__PURE__*/ devLog('📱 [WORKOUT EXECUTION] Page restored from bfcache');
        // Page was restored from cache, ensure state is correct
        setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [startTime]);

  const loadWorkoutData = async (hasExistingState = false) => {
    const t0 = performance.now();
    try {
      setLoading(true);
      /*#__PURE__*/ devLog('🏋️ [WORKOUT EXECUTION] Loading workout:', workoutId, '| hasExistingState:', hasExistingState);

      // If we already have state from lazy init, skip localStorage restore
      let hasRestoredSession = hasExistingState;

      // Only try localStorage if we don't have existing state
      if (!hasExistingState) {
        const savedSession = localStorage.getItem(getStorageKey());

        if (savedSession) {
          try {
            const sessionData = JSON.parse(savedSession);
            const sessionAge = Date.now() - sessionData.lastUpdate;

            // Restore if session < 24 hours old
            if (sessionAge < 24 * 60 * 60 * 1000) {
              /*#__PURE__*/ devLog('♻️ [WORKOUT EXECUTION] Found valid saved session, will restore');
              hasRestoredSession = true;
              setStartTime(sessionData.startTime);
              setExerciseStates(sessionData.exerciseStates);

              // CRITICAL: Restore extra exercises to prevent data loss
              if (sessionData.extraExercises && sessionData.extraExercises.length > 0) {
                /*#__PURE__*/ devLog('♻️ [WORKOUT EXECUTION] Restoring', sessionData.extraExercises.length, 'extra exercises');
                setExtraExercises(sessionData.extraExercises);
              }
            }
          } catch (e) {
            console.error('Error parsing saved session:', e);
          }
        }
      }

      // Load workout data (always needed for exercise info)
      const data = await getWorkoutWithExercises(workoutId);

      if (!data) {
        /*#__PURE__*/ devLog('⚠️ [WORKOUT EXECUTION] Workout not found');
        navigate('/athlete/program');
        return;
      }

      setWorkout(data);

      // PERFORMANCE: Show UI immediately, load history/suggestions in background
      // If we restored from session (or had existing state), we're done
      if (hasRestoredSession) {
        /*#__PURE__*/ devLog('♻️ [WORKOUT EXECUTION] Session restored in ', Math.round(performance.now() - t0), 'ms');
        setLoading(false);
      } else {
        // Initialize fresh state (only if no saved session)
        const initialStates = initializeExerciseStates(data.exercises);
        setExerciseStates(initialStates);
        setStartTime(Date.now());
        /*#__PURE__*/ devLog('✅ [WORKOUT EXECUTION] UI ready in ', Math.round(performance.now() - t0), 'ms');
        setLoading(false);
      }

      // PERFORMANCE: Load exercise history in background AFTER UI is shown
      if (data.exercises && data.exercises.length > 0) {
        const exerciseIds = data.exercises.map(ex => ex.id);
        // Use setTimeout to ensure UI renders first
        historyTimerRef.current = setTimeout(() => {
          Promise.all([
            getLastPerformedForExercises(profile.id, exerciseIds),
            getFirstPerformedForExercises(profile.id, exerciseIds)
          ])
            .then(([lastHistory, firstHistory]) => {
              if (!mountedRef.current) return; // pagina chiusa nel frattempo
              /*#__PURE__*/ devLog('📊 [WORKOUT EXECUTION] History loaded in ', Math.round(performance.now() - t0), 'ms');
              setExerciseHistory(lastHistory);
              setFirstPerformed(firstHistory);
              // Weight suggestions are optional enhancement, load lazily
              calculateWeightSuggestions(data.exercises, profile.id);
            })
            .catch(err => {
              console.warn('⚠️ [WORKOUT EXECUTION] History load failed (non-blocking):', err.message);
            });
        }, 100);
      }
    } catch (error) {
      console.error('❌ [WORKOUT EXECUTION] Error loading workout:', error);
      setLoading(false);
    }
  };

  const initializeExerciseStates = (exercises) => {
    const states = {};
    exercises.forEach(ex => {
      // Check if this is a cardio exercise
      const isCardio = isCardioType(ex);

      if (isCardio) {
        // Cardio: single "set" with cardio-specific fields
        states[ex.id] = {
          sets: [{
            setNumber: 1,
            completed: false,
            duration: '',
            distance: '',
            speed: '',
            incline: '',
            notes: '',
            expanded: false
          }]
        };
      } else {
        // Strength or time-based: multiple sets with reps/weight or duration/weight
        const numSets = parseInt(ex.sets) || 3;
        const suggestedWeight = ex.weight ? ex.weight.toString() : '';
        const isTimeBased = isTimeType(ex.exercise_type);

        states[ex.id] = {
          sets: Array.from({ length: numSets }, (_, i) => ({
            setNumber: i + 1,
            completed: false,
            reps: isTimeBased ? '' : '',
            duration: isTimeBased ? '' : undefined,
            weight: suggestedWeight,
            notes: '',
            expanded: false
          }))
        };
      }
    });
    return states;
  };

  // Calculate weight suggestions based on exercise history
  const calculateWeightSuggestions = async (exercises, athleteId) => {
    const suggestions = {};

    // Load history for each exercise and calculate suggestion
    await Promise.all(
      exercises.map(async (exercise) => {
        try {
          const history = await getExerciseProgressHistory(athleteId, exercise.id, 5);

          if (history && history.length >= 2) {
            // Transform history to format expected by generateWeightSuggestion
            const sessions = history.map(session => {
              const weights = session.sets.map(s => s.weight).filter(Boolean);
              const reps = session.sets.map(s => s.reps).filter(Boolean);

              return {
                weight: weights.length > 0 ? Math.max(...weights) : 0,
                reps: reps.length > 0 ? Math.round(reps.reduce((a, b) => a + b, 0) / reps.length) : 0,
                date: session.date,
                incrementStep: exercise.increment_step || null // ← NUOVO: Pass from exercise
              };
            }).filter(s => s.weight > 0 && s.reps > 0);

            if (sessions.length >= 2) {
              // Get target reps from exercise prescription
              const targetReps = exercise.reps ? parseInt(exercise.reps.toString().match(/\d+/)?.[0]) : null;
              const suggestion = generateWeightSuggestion(sessions, targetReps);

              if (suggestion.hasSuggestion) {
                suggestions[exercise.id] = formatSuggestion(suggestion);
              }
            }
          }
        } catch (err) {
          console.warn('Could not calculate suggestion for exercise:', exercise.id, err);
        }
      })
    );

    /*#__PURE__*/ devLog('💡 [WORKOUT EXECUTION] Weight suggestions calculated:', Object.keys(suggestions).length);
    setWeightSuggestions(suggestions);
  };

  // Render helper: card di un esercizio prescritto (usata sia standalone sia nel blocco superserie)
  const renderPrescribedCard = (exercise) => (
    <ExerciseExecutionCard
      exercise={exercise}
      setStates={exerciseStates[exercise.id]?.sets || []}
      onToggleSet={(setNumber) => handleToggleSet(exercise.id, setNumber)}
      onToggleAllSets={(newCompletedState) => handleToggleAllSets(exercise.id, newCompletedState)}
      onExpandSet={(setNumber) => handleExpandSet(exercise.id, setNumber)}
      onUpdateSet={(setNumber, field, value) => handleUpdateSet(exercise.id, setNumber, field, value)}
      onAddExtraSet={() => handleAddExtraSet(exercise.id)}
      onDeleteSet={(setNumber) => handleDeleteSet(exercise.id, setNumber)}
      onStartRestTimer={handleStartRestTimer}
      lastPerformed={exerciseHistory[exercise.id]}
      firstPerformed={firstPerformed[exercise.id]}
      suggestedWeight={weightSuggestions[exercise.id]}
      isExpanded={expandedExerciseId === exercise.id}
      onToggleExpand={() => setExpandedExerciseId(prev => prev === exercise.id ? null : exercise.id)}
    />
  );

  // Card di un esercizio EXTRA (aggiunto in sessione)
  const renderExtraCard = (exercise) => (
    <ExerciseExecutionCard
      exercise={exercise}
      setStates={exerciseStates[exercise.id]?.sets || []}
      onToggleSet={(setNumber) => handleToggleSet(exercise.id, setNumber)}
      onToggleAllSets={(newCompletedState) => handleToggleAllSets(exercise.id, newCompletedState)}
      onExpandSet={(setNumber) => handleExpandSet(exercise.id, setNumber)}
      onUpdateSet={(setNumber, field, value) => handleUpdateSet(exercise.id, setNumber, field, value)}
      onAddExtraSet={() => handleAddExtraSet(exercise.id)}
      onDeleteSet={(setNumber) => handleDeleteSet(exercise.id, setNumber)}
      onStartRestTimer={handleStartRestTimer}
      isExtraExercise={true}
      isExpanded={expandedExerciseId === exercise.id}
      onToggleExpand={() => setExpandedExerciseId(prev => prev === exercise.id ? null : exercise.id)}
    />
  );

  // Dispatcher: prescritto o extra
  const renderCard = (exercise) => exercise.isExtra ? renderExtraCard(exercise) : renderPrescribedCard(exercise);

  // Lista unificata prescritti + extra (per superserie fra qualsiasi gruppo)
  const allItems = [...(workout?.exercises || []), ...extraExercises];
  // Creazione/scioglimento superserie live (hook condiviso con FreeWorkout)
  const linking = useSupersetLinking({
    sessionSupersets,
    setSessionSupersets,
    orderedIds: allItems.map(e => e.id), // PB-008: ordine reale mostrato a schermo
  });

  // Auto-fill dei campi vuoti quando una serie viene completata
  const buildToggledSet = (set, exercise, willBeCompleted) => {
    let updatedSet = { ...set, completed: willBeCompleted };
    if (willBeCompleted) {
      if (!updatedSet.reps || updatedSet.reps === '' || updatedSet.reps === '0') {
        updatedSet.reps = exercise?.reps || '';
      }
      if (!updatedSet.weight || updatedSet.weight === '' || updatedSet.weight === '0') {
        updatedSet.weight = exercise?.weight || '';
      }
    }
    return updatedSet;
  };

  // Decisione recupero (deterministica, sullo stato mergiato previsto).
  // Nelle superserie il recupero parte SOLO a fine giro (serie N di tutti i membri).
  const maybeStartRest = (exerciseId, exercise, setNumber, statesAfter) => {
    const group = getSupersetForExercise(sessionSupersets, exerciseId);
    if (group) {
      const roundIndex = setNumber - 1;
      const shared = sharedRounds(group, statesAfter);
      const roundDone = roundIndex < shared && isRoundComplete(group, statesAfter, roundIndex);
      const beyondShared = roundIndex >= shared;
      if (roundDone || beyondShared) {
        // PB-006 + PB-011 (J4):
        // A superset never invents a default rest.
        // L'UNICA fonte è il recupero della superserie; se null/0 nessun timer.
        // Non si torna MAI al recupero individuale di un membro (anche oltre i
        // round condivisi, finché l'esercizio resta nel gruppo). Nessun 90.
        const rest = Number(resolveRestSeconds(group));
        if (rest > 0) handleStartRestTimer(rest);
      }
    } else if (exercise?.rest_seconds) {
      handleStartRestTimer(exercise.rest_seconds);
    }
  };

  const handleToggleSet = (exerciseId, setNumber) => {
    const exercise = [...(workout.exercises || []), ...extraExercises].find(ex => ex.id === exerciseId);
    const currentSet = exerciseStates[exerciseId]?.sets?.find(s => s.setNumber === setNumber);
    if (!currentSet) return;
    const willBeCompleted = !currentSet.completed;

    // Updater FUNZIONALE: setState con oggetto pre-calcolato si sovrascrive
    // quando più toggle arrivano nello stesso batch (restava solo l'ultimo).
    const applyToggle = (states) => {
      if (!states[exerciseId]?.sets) return states;
      return {
        ...states,
        [exerciseId]: {
          ...states[exerciseId],
          sets: states[exerciseId].sets.map(set =>
            set.setNumber === setNumber ? buildToggledSet(set, exercise, willBeCompleted) : set
          )
        }
      };
    };
    setExerciseStates(applyToggle);

    if (willBeCompleted) {
      maybeStartRest(exerciseId, exercise, setNumber, applyToggle(exerciseStates));
    }
  };

  // Spunta master dell'esercizio: completa/azzera TUTTE le serie in un unico
  // aggiornamento atomico; il recupero parte UNA volta, riferito all'ultima serie.
  const handleToggleAllSets = (exerciseId, newCompletedState) => {
    const exercise = [...(workout.exercises || []), ...extraExercises].find(ex => ex.id === exerciseId);
    const sets = exerciseStates[exerciseId]?.sets;
    if (!sets || sets.length === 0) return;

    const applyToggleAll = (states) => {
      if (!states[exerciseId]?.sets) return states;
      return {
        ...states,
        [exerciseId]: {
          ...states[exerciseId],
          sets: states[exerciseId].sets.map(set =>
            set.completed === newCompletedState ? set : buildToggledSet(set, exercise, newCompletedState)
          )
        }
      };
    };
    setExerciseStates(applyToggleAll);

    if (newCompletedState) {
      const lastSetNumber = Math.max(...sets.map(s => s.setNumber));
      maybeStartRest(exerciseId, exercise, lastSetNumber, applyToggleAll(exerciseStates));
    }
  };

  const handleExpandSet = (exerciseId, setNumber) => {
    setExerciseStates(prev => ({
      ...prev,
      [exerciseId]: {
        ...prev[exerciseId],
        sets: prev[exerciseId].sets.map(set =>
          set.setNumber === setNumber
            ? { ...set, expanded: !set.expanded }
            : set
        )
      }
    }));
  };

  const handleUpdateSet = (exerciseId, setNumber, field, value) => {
    setExerciseStates(prev => ({
      ...prev,
      [exerciseId]: {
        ...prev[exerciseId],
        sets: prev[exerciseId].sets.map(set =>
          set.setNumber === setNumber
            ? { ...set, [field]: value }
            : set
        )
      }
    }));
  };

  const handleAddExtraSet = (exerciseId) => {
    setExerciseStates(prev => {
      const currentSets = prev[exerciseId].sets;
      const nextSetNumber = currentSets.length + 1;

      // Get suggested weight from last completed set
      const lastCompletedSet = [...currentSets]
        .reverse()
        .find(s => s.completed && s.weight);
      const suggestedWeight = lastCompletedSet ? lastCompletedSet.weight : '';

      // Check if this is a time-based exercise
      const exercise = workout?.exercises?.find(ex => ex.id === exerciseId) ||
                      extraExercises.find(ex => ex.id === exerciseId);
      const isTimeBased = isTimeType(exercise?.exercise_type);

      return {
        ...prev,
        [exerciseId]: {
          ...prev[exerciseId],
          sets: [
            ...currentSets,
            {
              setNumber: nextSetNumber,
              completed: false,
              reps: isTimeBased ? '' : '',
              duration: isTimeBased ? '' : undefined,
              weight: suggestedWeight,
              notes: '',
              expanded: false,
              isExtra: true,
              extraType: 'extra_set'
            }
          ]
        }
      };
    });
  };

  const handleDeleteSet = (exerciseId, setNumber) => {
    const currentSets = exerciseStates[exerciseId]?.sets || [];

    // Don't allow deleting if it's the only set (unless it's an extra set)
    const isOnlySet = currentSets.length === 1;
    const setToDelete = currentSets.find(s => s.setNumber === setNumber);

    if (isOnlySet && !setToDelete?.isExtra) {
      return; // Don't delete the only prescribed set
    }

    // Filter out the set to delete
    const updatedSets = currentSets
      .filter(s => s.setNumber !== setNumber)
      .map((s, index) => ({
        ...s,
        setNumber: index + 1 // Renumber sets after deletion
      }));

    // Check if this is an extra exercise and now has 0 sets
    const isExtraExercise = exerciseId.startsWith('extra-');

    if (updatedSets.length === 0 && isExtraExercise) {
      // Remove the entire extra exercise from both states
      setExtraExercises(prev => prev.filter(ex => ex.id !== exerciseId));
      setExerciseStates(prev => {
        const newState = { ...prev };
        delete newState[exerciseId];
        return newState;
      });
    } else {
      // Update sets normally
      setExerciseStates(prev => ({
        ...prev,
        [exerciseId]: {
          ...prev[exerciseId],
          sets: updatedSets
        }
      }));
    }
  };

  const handleAddExtraExercise = ({ name, sets, target, weight, templateId, restSeconds, exerciseType, usesWeight }) => {
    // Create a temporary ID for the extra exercise
    const tempId = `extra-${Date.now()}`;
    const type = exerciseType || 'reps';
    const isTimeBased = isTimeType(type); // 'time' | 'hold'

    const newExtraExercise = {
      id: tempId,
      name,
      sets: parseInt(sets),
      reps: target, // per time/hold contiene i secondi target (placeholder card)
      weight: weight ? parseFloat(weight) : null,
      rest_seconds: restSeconds ? parseInt(restSeconds) : null,
      exercise_template_id: templateId, // Link to database template
      exercise_type: type,
      uses_weight: usesWeight, // rispettato da ExerciseExecutionCard (helper usesWeight)
      isExtra: true
    };

    setExtraExercises(prev => [...prev, newExtraExercise]);

    // Initialize state for this extra exercise
    const initialSets = Array.from({ length: parseInt(sets) }, (_, i) => ({
      setNumber: i + 1,
      completed: false,
      reps: '',
      duration: isTimeBased ? '' : undefined,
      weight: weight || '',
      notes: '',
      expanded: false,
      isExtra: true,
      extraType: 'extra_exercise'
    }));

    setExerciseStates(prev => ({
      ...prev,
      [tempId]: {
        sets: initialSets
      }
    }));

    setShowExtraExerciseModal(false);
  };

  const getProgress = () => {
    let total = 0;
    let completed = 0;

    Object.values(exerciseStates).forEach(ex => {
      total += ex.sets.length;
      completed += ex.sets.filter(s => s.completed).length;
    });

    return { total, completed };
  };

  const hasCompletedSets = () => {
    return Object.values(exerciseStates).some(ex =>
      ex.sets.some(set => set.completed)
    );
  };

  const handleCompleteWorkout = async () => {
    if (!hasCompletedSets()) {
      toast.warning('Completa almeno una serie prima di salvare l\'allenamento!');
      return;
    }

    // IDEMPOTENCY: Prevent duplicate saves (ref = guardia sincrona, lo stato
    // React non è ancora aggiornato se il secondo tap arriva nello stesso frame)
    if (saving || savingRef.current) {
      /*#__PURE__*/ devLog('⚠️ [WORKOUT EXECUTION] Already saving, ignoring duplicate tap');
      return;
    }

    if (!window.confirm('Vuoi completare questo allenamento?')) {
      return;
    }

    if (savingRef.current) return; // tap accodato durante il confirm
    savingRef.current = true;

    setSaving(true);
    /*#__PURE__*/ devLog('💾 [WORKOUT EXECUTION] Saving workout... (clientSessionId:', clientSessionId, ')');

    // Pre-refresh token before saving (token may have expired during long workout)
    try {
      /*#__PURE__*/ devLog('🔄 [WORKOUT EXECUTION] Refreshing session before save...');
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.warn('⚠️ [WORKOUT EXECUTION] Session refresh warning:', error.message);
      } else if (data?.session) {
        /*#__PURE__*/ devLog('✅ [WORKOUT EXECUTION] Session refreshed successfully');
      }
    } catch (refreshErr) {
      console.warn('⚠️ [WORKOUT EXECUTION] Could not refresh session:', refreshErr.message);
    }

    // Build exercise logs
    const exerciseLogs = [];
    const exerciseMap = {};

    if (workout && workout.exercises) {
      workout.exercises.forEach(ex => {
        exerciseMap[ex.id] = ex;
      });
    }
    extraExercises.forEach(ex => {
      exerciseMap[ex.id] = ex;
    });

    Object.entries(exerciseStates).forEach(([exerciseId, exState]) => {
      const exercise = exerciseMap[exerciseId];
      const isExtraExercise = exerciseId.startsWith('extra-');
      const isCardio = isCardioType(exercise);
      const isTimeBased = isTimeType(exercise?.exercise_type);

      exState.sets.forEach(set => {
        // Check if set has data (different criteria for cardio vs strength vs time)
        const hasCardioData = isCardio && (set.completed || set.duration || set.distance || set.speed || set.incline);
        const hasTimeBasedData = isTimeBased && (set.completed || set.duration || set.weight || set.notes);
        const hasStrengthData = !isCardio && !isTimeBased && (set.completed || set.reps || set.weight || set.notes);

        if (hasCardioData || hasTimeBasedData || hasStrengthData) {
          // FIX: Initialize ALL fields to ensure uniform payload (Supabase bulk insert requires all objects to have same keys)
          const logEntry = {
            setNumber: set.setNumber,
            notes: set.notes || null,
            isExtra: set.isExtra || false,
            extraType: set.extraType || null,
            exerciseName: exercise?.name || null,
            // Initialize ALL possible fields to null (will be set below based on exercise type)
            reps: null,
            weight: null,
            duration_minutes: null,
            duration_seconds: null,
            distance: null,
            speed: null,
            incline: null
          };

          if (isCardio) {
            // Cardio exercise: save duration/distance/speed/incline
            logEntry.reps = 1; // FIX: Database requires reps NOT NULL - use 1 as placeholder for "1 cardio session completed"
            logEntry.duration_minutes = set.duration ? parseInt(set.duration) : (set.completed ? exercise.duration_minutes : null);
            logEntry.distance = set.distance ? parseFloat(set.distance) : null;
            logEntry.speed = set.speed ? parseFloat(set.speed) : null;
            logEntry.incline = set.incline ? parseFloat(set.incline) : null;
          } else if (isTimeBased) {
            // Time-based exercise (e.g., plank, wall sit): save duration_seconds
            let durationValue = null;
            if (set.duration) {
              durationValue = parseInt(set.duration);
            } else if (set.completed && exercise) {
              const targetDuration = exercise.reps;
              if (targetDuration) {
                const match = targetDuration.toString().match(/\d+/);
                durationValue = match ? parseInt(match[0]) : null;
              }
            }

            if (durationValue === null) {
              return; // Skip time-based sets without duration
            }

            logEntry.reps = durationValue; // Store in reps field for backward compatibility
            logEntry.duration_seconds = durationValue; // Also store in new field
            logEntry.weight = set.weight ? parseFloat(set.weight) : null;
          } else {
            // Strength exercise: save reps/weight
            let repsValue = null;
            if (set.reps) {
              repsValue = parseInt(set.reps);
            } else if (set.completed && exercise) {
              const targetReps = exercise.reps;
              if (targetReps) {
                const match = targetReps.toString().match(/\d+/);
                repsValue = match ? parseInt(match[0]) : null;
              }
            }

            if (repsValue === null && !isCardio) {
              return; // Skip strength sets without reps
            }

            logEntry.reps = repsValue;
            logEntry.weight = set.weight ? parseFloat(set.weight) : null;
          }

          // FIX: CONSTRAINT: exercise_id XOR exercise_template_id (one must be set, not both!)
          if (isExtraExercise) {
            // Extra exercise: use template_id only
            logEntry.exerciseId = null;
            // FIX: For extra exercises, exercise_template_id MUST NOT be null - use a fallback if missing
            if (!exercise?.exercise_template_id) {
              console.error('❌ [WORKOUT EXECUTION] Extra exercise missing exercise_template_id:', exerciseId, exercise?.name);
              toast.error(`Errore: esercizio "${exercise?.name || 'sconosciuto'}" non valido. Rimuovilo e ricrealo.`);
              return; // Skip this log - it would violate constraint
            }
            logEntry.exerciseTemplateId = exercise.exercise_template_id;
          } else {
            // Normal exercise: use exercise_id only (NOT template_id!)
            logEntry.exerciseId = exerciseId;
            logEntry.exerciseTemplateId = null; // Must be null for normal exercises
          }

          exerciseLogs.push(logEntry);
        }
      });
    });

    // Validate: must have at least one exercise log
    if (exerciseLogs.length === 0) {
      toast.error('Errore: nessun dato esercizio da salvare. Completa almeno una serie.');
      savingRef.current = false;
      setSaving(false);
      return;
    }

    const durationMinutes = Math.floor(elapsedSeconds / 60);

    try {
      const workoutLog = await saveCompleteWorkout(
        profile.id,
        workoutId,
        durationMinutes,
        null,
        exerciseLogs,
        clientSessionId // PB-013: chiave di idempotency (UNIQUE a DB, retry sicuri)
      );

      /*#__PURE__*/ devLog('✅ [WORKOUT EXECUTION] Workout saved successfully');

      // Clear localStorage only on success
      localStorage.removeItem(getStorageKey());

      // Workout chiuso: nessun recupero deve sopravvivere (né timer persistito
      // né push programmata, che altrimenti arriverebbe ad allenamento finito)
      setIsRestTimerActive(false);
      clearPersistedRestTimer();
      cancelPushForRest();

      // Invalidate caches
      try {
        invalidateCache(`athlete-dashboard-*`);
        invalidateCache(`athlete-workout-logs-*`);
        invalidateCache(`athlete-volume-*`);
        invalidateCache(`athlete-exercise-records-*`);
      } catch (e) {
        console.warn('⚠️ Cache invalidation warning:', e);
      }

      // Riepilogo dai dati già in memoria (nessuna query): il popup di fine
      // workout deve comparire SEMPRE, anche se XP o badge falliscono.
      const workoutSummary = buildWorkoutSummary(exerciseLogs, durationMinutes);

      // Award XP (non-blocking: failure never prevents navigation)
      let xpResult = null;
      try {
        const xpBreakdown = calculateWorkoutXP({ durationMinutes, completedSets: exerciseLogs.length });
        xpResult = await awardWorkoutXP(profile.id, workoutLog.id, xpBreakdown.total);
        xpResult.xpBreakdown = xpBreakdown;
        /*#__PURE__*/ devLog('⭐ [XP] Awarded', xpBreakdown.total, 'XP → Level', xpResult.currentLevel);
      } catch (xpErr) {
        console.warn('⚠️ [XP] Could not award XP (non-blocking):', xpErr.message);
      }

      // Badge subito dopo XP (i badge di livello vedono i totali aggiornati);
      // in retry/doppio completamento ritorna [] → nessuna doppia notifica.
      let newBadges = [];
      try {
        newBadges = await evaluateBadgesAfterWorkout(profile.id);
      } catch (badgeErr) {
        console.warn('⚠️ [BADGE] Could not evaluate badges (non-blocking):', badgeErr.message);
      }

      navigate('/athlete', { state: { workoutCompleted: true, xpResult, workoutSummary, newBadges, workoutName: workout?.name || '' } });
    } catch (error) {
      console.error('❌ [WORKOUT EXECUTION] Error saving workout:', error);

      // More specific error message
      const errorMsg = error.message || 'Errore sconosciuto';
      if (errorMsg.includes('duplicate') || errorMsg.includes('already exists')) {
        // Workout already saved - this is actually success!
        /*#__PURE__*/ devLog('ℹ️ [WORKOUT EXECUTION] Workout was already saved (duplicate detected)');
        localStorage.removeItem(getStorageKey());
        setIsRestTimerActive(false);
        clearPersistedRestTimer();
        cancelPushForRest();
        navigate('/athlete', {
          state: {
            workoutCompleted: true,
            workoutSummary: buildWorkoutSummary(exerciseLogs, durationMinutes),
            newBadges: [],
            workoutName: workout?.name || '',
          },
        });
        return;
      }

      toast.error(`Errore nel salvataggio dell'allenamento: ${errorMsg}. I tuoi dati sono salvati localmente. Riprova.`, 5000);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleExit = () => {
    if (hasCompletedSets()) {
      if (!window.confirm('Hai dati non salvati. Sei sicuro di voler uscire?')) {
        return;
      }
    }

    // PERFORMANCE: Navigate immediately, cleanup async
    navigate('/athlete');

    // Cleanup in background (non-blocking): anche il recupero in corso muore
    // con il workout (timer persistito + push programmata)
    setTimeout(() => {
      try {
        localStorage.removeItem(getStorageKey());
        clearPersistedRestTimer();
        cancelPushForRest();
      } catch (e) {
        console.warn('Cleanup warning:', e);
      }
    }, 0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartRestTimer = (seconds = 90) => {
    // Chiamato in sincrono dal tap che completa la serie → gesto utente valido
    // per sbloccare l'audio iOS (altrimenti il beep di fine recupero resta muto).
    unlockAudioAlert();
    // Nuova serie = nuovo recupero: azzera lo stato persistito del timer
    // precedente PRIMA del remount, altrimenti RestTimer "ripristina" il vecchio
    // countdown (vecchia scadenza e vecchio timerId, push non riprogrammata).
    // La push vecchia muore da sé: il ramo fresh riprogramma con un nuovo
    // timerId sulla stessa riga rest_push_state (upsert per user_id).
    clearPersistedRestTimer();
    setRestTimerSeconds(seconds);
    setTimerKey(prev => prev + 1);
    setIsRestTimerActive(true);
    // Primo recupero in assoluto → proponi le notifiche (una volta sola)
    if (shouldShowPushPrePrompt()) setShowPushPrePrompt(true);
  };

  const handleCloseRestTimer = () => {
    setIsRestTimerActive(false);
  };

  const handleClosePushPrePrompt = () => {
    // Qualunque esito (Attiva / Non ora / chiusura): mai riproporre in automatico
    markPushPrePromptDone();
    setShowPushPrePrompt(false);
  };

  if (loading) {
    return (
      <div className="workout-execution-container">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          Caricamento...
        </div>
      </div>
    );
  }

  if (!workout) {
    return (
      <div className="workout-execution-container">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>Allenamento non trovato</p>
          <button onClick={() => navigate('/athlete/program')}>Torna alla Scheda</button>
        </div>
      </div>
    );
  }

  const progress = getProgress();
  const canComplete = hasCompletedSets();

  return (
    <>
      {/* Rest Timer - rendered via portal to body */}
      {isRestTimerActive && ReactDOM.createPortal(
        <RestTimer
          key={timerKey}
          isActive={true}
          onClose={handleCloseRestTimer}
          initialSeconds={restTimerSeconds}
        />,
        document.body
      )}

      {/* Pre-prompt notifiche (solo al primo recupero di sempre) */}
      {showPushPrePrompt && <RestPushPrePrompt onClose={handleClosePushPrePrompt} />}

      <div className="min-h-screen bg-gray-50 flex flex-col" style={{ paddingTop: isRestTimerActive ? '76px' : '0' }}>
        <header className="sticky bg-white shadow-lg z-50 px-4 py-4" style={{ top: isRestTimerActive ? '76px' : '0' }}>
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <button
            onClick={handleExit}
            className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg font-semibold text-sm hover:bg-red-100 transition-colors"
          >
            ← Esci
          </button>
          <div className="flex flex-col items-center">
            <h1 className="text-xl font-extrabold text-gray-900 uppercase tracking-wide">{workout.name}</h1>
            <div className="font-mono text-2xl font-bold text-primary-500 tabular-nums">
              {formatTime(elapsedSeconds)}
            </div>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-4xl mx-auto px-4 py-5" style={{ paddingBottom: 'max(220px, calc(180px + env(safe-area-inset-bottom, 0px)))' }}>
        <div className="flex flex-col gap-4">
          {/* Esercizi (prescritti + extra) raggruppati per superserie di sessione */}
          <SupersetList
            items={allItems}
            sessionSupersets={sessionSupersets}
            renderCard={renderCard}
            linking={linking}
          />

          {allItems.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
              <p>Nessun esercizio in questo workout</p>
            </div>
          )}
        </div>
      </main>

      <footer className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-white via-white to-white/0 px-4 pt-4 shadow-2xl" style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))' }}>
        <div className="max-w-4xl mx-auto space-y-3">
          {/* PB-009: durante la modalità collega superserie il footer ospita la
              barra di conferma (sempre visibile in basso, vicino agli esercizi
              selezionati); progress e COMPLETA tornano appena si esce/conferma. */}
          {linking.linkMode ? (
            <SupersetLinkBar
              linkMode={linking.linkMode}
              count={linking.selection.length}
              onConfirm={linking.confirm}
              onCancel={linking.cancel}
            />
          ) : (
          <>
          {/* Progress bar */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full gradient-success transition-all duration-500 ease-out"
                style={{ width: `${progress.total > 0 ? (progress.completed / progress.total) * 100 : 0}%` }}
              />
            </div>
            <span className="text-sm font-bold text-gray-600 tabular-nums whitespace-nowrap">
              {progress.completed}/{progress.total}
            </span>
          </div>

          {/* Buttons row */}
          <div className="flex gap-3">
            {/* Add menu (Aggiungi esercizio / Crea superserie) */}
            <button
              onClick={() => setShowAddMenu(true)}
              className="px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium text-sm hover:bg-gray-200 transition-all active:scale-98 flex items-center gap-2"
            >
              <span className="text-lg">+</span>
              <span className="hidden sm:inline">Aggiungi</span>
            </button>

            {/* Complete - Primary, prominent */}
            <button
              onClick={handleCompleteWorkout}
              disabled={!canComplete || saving}
              className="flex-1 py-4 px-6 gradient-success text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-98"
            >
              {saving ? 'Salvataggio...' : '✓ COMPLETA'}
            </button>
          </div>
          </>
          )}
        </div>
      </footer>

        {/* Menu del pulsante "+" */}
        {showAddMenu && (
          <AddActionsSheet
            onClose={() => setShowAddMenu(false)}
            canCreateSuperset={allItems.length >= 2}
            onAddExercise={() => { setShowAddMenu(false); setShowExtraExerciseModal(true); }}
            onCreateSuperset={() => { setShowAddMenu(false); linking.enter(); }}
          />
        )}

        {/* Extra Exercise Modal */}
        {showExtraExerciseModal && (
          <AddExerciseSheet
            title="Aggiungi Esercizio Extra"
            onClose={() => setShowExtraExerciseModal(false)}
            onAdd={handleAddExtraExercise}
            profile={profile}
            isRestTimerActive={isRestTimerActive}
            defaultSets="3"
            defaultRestSeconds=""
          />
        )}
      </div>
    </>
  );
}

export default WorkoutExecution;
